import React from 'react';

interface HawkConfig {
  authId: string;
  authKey: string;
  algorithm: 'sha256' | 'sha1';
  ext?: string;
  includePayloadHash: boolean;
}

interface HawkAuthProps {
  config: HawkConfig;
  onChange: (config: HawkConfig) => void;
}

export function HawkAuth({ config, onChange }: HawkAuthProps) {
  const handleChange = (updates: Partial<HawkConfig>) => {
    onChange({ ...config, ...updates });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        HAWK authentication uses HMAC-based signatures for request authentication.
      </p>

      {/* Auth ID */}
      <div>
        <label className="block text-sm font-medium mb-1">Hawk Auth ID</label>
        <input
          type="text"
          value={config.authId || ''}
          onChange={(e) => handleChange({ authId: e.target.value })}
          placeholder="your-auth-id"
          className="w-full px-3 py-2 bg-background border border-input rounded-md font-mono text-sm"
        />
      </div>

      {/* Auth Key */}
      <div>
        <label className="block text-sm font-medium mb-1">Hawk Auth Key</label>
        <input
          type="text"
          value={config.authKey || ''}
          onChange={(e) => handleChange({ authKey: e.target.value })}
          placeholder="your-secret-key or {{variable}}"
          className="w-full px-3 py-2 bg-background border border-input rounded-md font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Can use variables like {"{{hawkSecret}}"}
        </p>
      </div>

      {/* Algorithm */}
      <div>
        <label className="block text-sm font-medium mb-1">Algorithm</label>
        <select
          value={config.algorithm}
          onChange={(e) =>
            handleChange({ algorithm: e.target.value as HawkConfig['algorithm'] })
          }
          className="w-full px-3 py-2 bg-background border border-input rounded-md"
        >
          <option value="sha256">SHA-256 (Recommended)</option>
          <option value="sha1">SHA-1</option>
        </select>
      </div>

      {/* Ext */}
      <div>
        <label className="block text-sm font-medium mb-1">
          Ext <span className="text-muted-foreground">(optional application-specific data)</span>
        </label>
        <input
          type="text"
          value={config.ext || ''}
          onChange={(e) => handleChange({ ext: e.target.value })}
          placeholder="application-specific-data"
          className="w-full px-3 py-2 bg-background border border-input rounded-md font-mono text-sm"
        />
      </div>

      {/* Include Payload Hash */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="includePayloadHash"
          checked={config.includePayloadHash}
          onChange={(e) => handleChange({ includePayloadHash: e.target.checked })}
          className="w-4 h-4 rounded border-input"
        />
        <label htmlFor="includePayloadHash" className="text-sm">
          Include payload hash in signature
        </label>
      </div>
    </div>
  );
}

export default HawkAuth;
